import { FC, ReactNode } from 'react';
import {
  IconDistancingLarge,
  IconDistancingMedium,
  IconDistancingSmall,
  IconProtectingLarge,
  IconProtectingMedium,
  IconProtectingSmall,
  IconSanitizingLarge,
  IconSanitizingMedium,
  IconSanitizingSmall,
  IconScreeningLarge,
  IconScreeningMedium,
  IconScreeningSmall,
} from './safetyMeasuresIcons';
import { TagType } from 'generated/graphql';
import { assertNever } from '@tock/shared/utils/typeUtils';

type SafetyMeasureIconSizes = 'large' | 'medium' | 'small';

export type SafetyMeasureCategoryType = Extract<
  TagType,
  | 'COVID_SAFETY_MEASURE_SCREENING'
  | 'COVID_SAFETY_MEASURE_PPE'
  | 'COVID_SAFETY_MEASURE_DISTANCING'
  | 'COVID_SAFETY_MEASURE_SANITIZING'
>;

const safetyMeasureIconFromSize = (
  size: SafetyMeasureIconSizes,
  small: FC,
  medium: FC,
  large: FC
): FC => {
  switch (size) {
    case 'small':
      return small;
    case 'medium':
      return medium;
    case 'large':
      return large;
    /* istanbul ignore next */
    default:
      assertNever(size);
  }
};

export const safetyMeasureIconFromTypeAndSize = (
  type: SafetyMeasureCategoryType,
  size: SafetyMeasureIconSizes
): ReactNode => {
  let IconComponent;
  switch (type) {
    case 'COVID_SAFETY_MEASURE_SCREENING':
      IconComponent = safetyMeasureIconFromSize(
        size,
        IconScreeningSmall,
        IconScreeningMedium,
        IconScreeningLarge
      );
      break;
    case 'COVID_SAFETY_MEASURE_PPE':
      IconComponent = safetyMeasureIconFromSize(
        size,
        IconProtectingSmall,
        IconProtectingMedium,
        IconProtectingLarge
      );
      break;
    case 'COVID_SAFETY_MEASURE_DISTANCING':
      IconComponent = safetyMeasureIconFromSize(
        size,
        IconDistancingSmall,
        IconDistancingMedium,
        IconDistancingLarge
      );
      break;
    case 'COVID_SAFETY_MEASURE_SANITIZING':
      IconComponent = safetyMeasureIconFromSize(
        size,
        IconSanitizingSmall,
        IconSanitizingMedium,
        IconSanitizingLarge
      );
      break;
    default:
      assertNever(type);
  }
  return <IconComponent />;
};

export const safetyMeasureCategoryNameFromType = (type: SafetyMeasureCategoryType): string => {
  switch (type) {
    case 'COVID_SAFETY_MEASURE_SCREENING':
      return 'Screening';
    case 'COVID_SAFETY_MEASURE_PPE':
      return 'Protecting';
    case 'COVID_SAFETY_MEASURE_DISTANCING':
      return 'Distancing';
    case 'COVID_SAFETY_MEASURE_SANITIZING':
      return 'Sanitizing';
    default:
      assertNever(type);
  }
};

export interface SafetyMeasureByCategory {
  type: SafetyMeasureCategoryType;
  measures: string[];
}

export const mockSafetyMeasures: SafetyMeasureByCategory[] = [
  {
    type: 'COVID_SAFETY_MEASURE_SCREENING',
    measures: [
      'Proof of vaccination required',
      'Contact tracing information collected',
      'Guest temperature check required',
      'Staff temperature check required',
      'Sick staff prohibited in the workplace',
    ],
  },
  {
    type: 'COVID_SAFETY_MEASURE_PPE',
    measures: [
      'Guests must wear masks unless eating or drinking',
      'Provides masks to guests',
      'Staff wears masks',
      'Staff wears gloves',
    ],
  },
  {
    type: 'COVID_SAFETY_MEASURE_DISTANCING',
    measures: [
      'Limited number of guests',
      'Distancing maintained in common areas',
      'Extra space between tables',
      'Designated pick-up area',
    ],
  },
  {
    type: 'COVID_SAFETY_MEASURE_SANITIZING',
    measures: [
      'Cash not accepted',
      'Sanitizer or wipes provided for guests',
      'Surfaces santitized between seatings',
      'Upgraded air filtration / purification systems',
      'Common areas deep cleaned daily',
      'Increased cleaning / disinfecting in the back-of-house',
      'Cleaning logs',
      'Digital, disposable, or sanitized menu provided',
      'Contactless payment available',
      'Staff training on up-to-date safety information and precautions',
      'Added food safety protocols',
    ],
  },
];
